import { z } from 'zod';
/**
 * ISO 8601 timestamp string. Firestore Timestamps serialize to this at the API boundary.
 */
export const IsoTimestamp = z.string().datetime({ offset: true });
/**
 * Firestore document ID. Opaque string, 1-1500 chars, no slashes.
 */
export const DocId = z
    .string()
    .min(1)
    .max(1500)
    .regex(/^[^/]+$/);
/**
 * Tenant ID. Lower-case slug of the tenant organisation.
 * `suppohrt` is the primary tenant. Each zorggroep gets its own (e.g. `rhogo`).
 *
 * Reserved special form: `_platform` — used by audit_log + system_status
 * writes that are not bound to a real tenant (cross-tenant admin actions
 * on shared collections like kennisbank_ring1). The leading underscore
 * is intentional and reserved; no real tenant slug may start with one.
 */
export const TenantId = z
    .string()
    .min(2)
    .max(64)
    .regex(/^_?[a-z][a-z0-9-]*$/, 'Tenant ID must be lowercase alphanumeric with hyphens (or `_platform` for cross-tenant system writes)');
/**
 * User role.
 *
 * Capability tiers (for tiebreaking when a user is in multiple Entra groups):
 *   kb_admin           — platform admin. Full CRUD on every admin surface.
 *   sensitive_user     — cleared for HR/employee-PII restricted content.
 *   confidential_user  — cleared for customer/invoice confidential content
 *                        (CS team, ExCo, Support). PEER of sensitive_user
 *                        in capability — HR clearance is not customer-data
 *                        clearance and vice-versa. Tier 1.5 (2026-05-20).
 *   exco_user          — leadership tier; sees exco-tagged content.
 *   kb_user            — base internal user. Sees Ring 0 + Ring 1 + Ring 2.
 *
 * v1 stores ONE role per user; combinatorial roles (a user who needs both
 * sensitive AND confidential) are deferred to Phase 1.2 (see
 * docs/designs/2026-05-08-onenebula-three-group-rbac.md). Until then a
 * user mapped to both groups gets the higher-priority winner per
 * ROLE_PRIORITY in group-mapping.ts. Sensitive intentionally outranks
 * confidential in the tiebreak because losing HR clearance unexpectedly
 * is a worse failure mode than losing invoice-data clearance.
 *
 * Heritage: this enum replaced suppohrt_admin / suppohrt_medewerker /
 * zorggroep_admin / zorggroep_gebruiker on the OneNebula cutover.
 * OneNebula is internal-only — partner-tier roles (zorggroep_*) were
 * dropped without a 1:1 successor. Existing user docs are migrated by
 * backend/scripts/migrate-user-roles.ts.
 */
export const UserRole = z.enum([
    'kb_user',
    'exco_user',
    'confidential_user',
    'sensitive_user',
    'kb_admin',
]);
/**
 * Content trust ring.
 *
 *   1 — OneNebula authored (highest trust, internal product/policy docs)
 *   2 — Verified external source (curated whitelist)
 *   3 — Unverified (DISABLED AT LAUNCH per /office-hours decision)
 */
export const ContentRing = z.union([z.literal(1), z.literal(2), z.literal(3)]);
/**
 * Document access level — enforced at retrieval time.
 *
 *   clinical — visible to all authenticated users.
 *   internal — visible to every authenticated kb_* role (post-cutover
 *              OneNebula is internal-only, so this is effectively
 *              "any authenticated user").
 */
export const AccessLevel = z.enum(['clinical', 'internal']);
/**
 * Helper: does a given role have internal-document access?
 * Post-cutover every authenticated role is internal; the helper is
 * retained so the Ring 0 gate keeps a single named decision point.
 */
export function roleCanSeeInternal(role) {
    return (role === 'kb_admin' ||
        role === 'kb_user' ||
        role === 'exco_user' ||
        role === 'sensitive_user' ||
        role === 'confidential_user');
}
/**
 * Helper: can a given role see customer-confidential content (invoices,
 * billing data, customer-PII)? Peer of `roleCanSeeSensitive` — neither
 * subsumes the other. Used for corpus-classification visibility checks.
 */
export function roleCanSeeConfidential(role) {
    return role === 'kb_admin' || role === 'confidential_user';
}
/**
 * Helper: can a given role use the Data Extractor (upload + run +
 * download + browse templates)? Open to every authenticated
 * knowledgebase role — administering templates (create / edit /
 * activate / deactivate) still requires `kb_admin`, enforced
 * separately in the template-mutation routes.
 *
 * 2026-06-10 broadened from `roleCanSeeConfidential` (admin + confidential_user
 * only) to all roles so the whole company can use the extractor.
 */
export function roleCanUseExtractor(role) {
    return (role === 'kb_admin' ||
        role === 'kb_user' ||
        role === 'exco_user' ||
        role === 'sensitive_user' ||
        role === 'confidential_user');
}
/**
 * Helper: can a given role see HR/employee-PII sensitive content?
 * Peer of `roleCanSeeConfidential`.
 */
export function roleCanSeeSensitive(role) {
    return role === 'kb_admin' || role === 'sensitive_user';
}
/**
 * Helper: can a given role administer the platform?
 */
export function roleIsAdmin(role) {
    return role === 'kb_admin';
}
/**
 * Helper: is a role cross-tenant (can view/manage other tenants)?
 * Only kb_admin qualifies. All other roles are tenant-scoped.
 */
export function roleIsCrossTenant(role) {
    return role === 'kb_admin';
}
/**
 * Standard metadata every Firestore doc carries.
 * Not all docs have updatedBy (first-write only sets createdBy).
 */
export const AuditMeta = z.object({
    createdAt: IsoTimestamp,
    createdBy: DocId,
    updatedAt: IsoTimestamp.optional(),
    updatedBy: DocId.optional(),
});
//# sourceMappingURL=common.js.map